import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import Input from "../../components/form-item/Input";
import MoneyInput from "../../components/form-item/MoneyInput";
import Modal from "../../components/modal/Modal";
import UserAvatar from "../../components/avatar/UserAvatar";
import AvatarStack, {
  AvatarStackItem,
} from "../../components/avatar-stack/AvatarStack";
import { useSearchAdminUsersQuery } from "../../app/services/adminBalance";
import { useCreditUserBalanceMutation } from "../../app/services/adminBalance";
import type { FollowUser } from "../../app/services/follows";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatYen, toYen } from "../../utils/money";

/** 検索結果のページサイズ。`/api/admin/users` を offset/limit で順次フェッチする。 */
const SEARCH_PAGE_LIMIT = 30;

/** 検索クエリ入力 → API 発火までのデバウンス時間 (ms)。 */
const SEARCH_DEBOUNCE_MS = 250;

/** 残高付与額の下限（円・税抜）。BE の ``MIN_AMOUNT`` と合わせる。 */
const MIN_CREDIT_AMOUNT = 100;

const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap16",
})`
  padding: 24px 16px 80px;
  max-width: 720px;
  margin: 0 auto;
`;

const Section = styled.section.attrs({
  className: "u-col u-w100 u-gap12",
})`
  padding: 16px;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  background: #fff;
`;

const SectionHeader = styled.div.attrs({
  className: "u-row u-w100 u-gap8 u-align-center",
})``;

const SectionTitle = styled.h2.attrs({ className: "heading-13" })`
  color: var(--soremo-text);
`;

const Count = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const Hint = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
`;

const SearchBar = styled.div.attrs({
  className: "u-row u-w100 u-gap8 u-align-center",
})``;

/**
 * `CreatorRolesManager` と同じく、可視領域に入ったら次ページを取得する
 * IntersectionObserver の sentinel。高さ 1px だと検出が不安定なため余裕を
 * 持たせ、rootMargin: 200px 側で先読みも併用する。
 */
const Sentinel = styled.div`
  width: 100%;
  height: 24px;
`;

const SelectedCard = styled.div.attrs({
  className: "u-row u-w100 u-gap12 u-align-center",
})`
  padding: 12px;
  border: 1px solid var(--soremo-blue);
  background: var(--soremo-bg-blue, rgba(0, 154, 206, 0.06));
  border-radius: 6px;
`;

const SelectedMeta = styled.div.attrs({ className: "u-col u-fill u-gap2" })``;

const SelectedName = styled.span.attrs({ className: "heading-13" })`
  color: var(--soremo-text);
`;

const SelectedSub = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

// === 金額入力（オファー作成の対価入力と同じスタイル） ===
//
// `MoneyInput suffix=""` で内部の「円」表記を消し、隣に <InlineUnit>円</InlineUnit>
// を置く。プレースホルダーと suffix の二重表示を避けつつ、`DetailFormStep`
// （対価入力）と視覚的に一貫させる。
const FieldRow = styled.div.attrs({ className: "u-col u-w100 u-gap8" })``;

const FieldLabelRow = styled.label.attrs({
  className: "bodytext-13 u-line-height-100 u-pl4",
})`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  color: var(--soremo-text);
`;

const RequiredBadge = styled.span.attrs({
  className: "label8 u-pl8 u-text-blue",
})``;

const OptionalBadge = styled.span.attrs({
  className: "label8 u-pl8 u-text-light-gray",
})``;

const FieldLabel: React.FC<{
  text: string;
  option?: "required" | "optional";
}> = ({ text, option }) => (
  <FieldLabelRow>
    {text}
    {option === "required" && <RequiredBadge>必須</RequiredBadge>}
    {option === "optional" && <OptionalBadge>任意</OptionalBadge>}
  </FieldLabelRow>
);

const AmountInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
`;

const InlineUnit = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  white-space: nowrap;
`;

// === 確認モーダル内の項目一覧 ===
const ConfirmList = styled.dl.attrs({
  className: "u-col u-w100 u-gap8",
})`
  margin: 0;
`;

const ConfirmRow = styled.div.attrs({
  className: "u-row u-w100 u-justify-between u-align-center",
})`
  gap: 12px;
`;

const ConfirmKey = styled.dt.attrs({ className: "bodytext-13" })`
  color: var(--soremo-gray);
  margin: 0;
`;

const ConfirmValue = styled.dd.attrs({ className: "heading-13" })`
  color: var(--soremo-text);
  margin: 0;
  text-align: right;
  word-break: break-word;
`;

const toAvatarStackItem = (user: FollowUser): AvatarStackItem => ({
  id: user.id,
  userId: user.id,
  avatar: user.avatar_url ?? undefined,
  displayName: user.display_name_jp,
});

/**
 * 管理者（ADMIN = ``master_admin``）専用の残高付与ページ。
 *
 * UI 構造は `CreatorRolesManager`（ロール×ユーザー管理）と揃え、検索 Input +
 * AvatarStack（単一選択）+ 無限スクロールで付与対象を選ぶ。検索対象は BE 側で
 * `master_client` / `admin(CREATOR)` のみに絞られている（master_admin / curator
 * は除外）。
 *
 * 残高表示との関係:
 *  - 入力額は新モデルの `balance_free` にそのまま加算される（税抜・整数円）。
 *  - `GET /api/me` 経由でアカウントポップオーバーに表示される残高は
 *    `(free + settled) × 1.1` の税込換算となるため、付与額より大きく表示される。
 *  - 旧モデル（`is_new_payment_model=False`）のプロジェクトはこの残高を参照しないため、
 *    旧モデル運用のユーザーには本機能で付与しないこと。
 */
const AdminBalanceCredit: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  // ===== 検索クエリ（CreatorRolesManager と同じ無限スクロール構成） =====
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();

  const [searchOffset, setSearchOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<FollowUser[]>([]);

  // クエリが変わったら offset と累積を初期化
  useEffect(() => {
    setSearchOffset(0);
    setAccumulated([]);
  }, [trimmedQuery]);

  const { data: searchResults, isFetching: isSearching } =
    useSearchAdminUsersQuery({
      q: trimmedQuery,
      limit: SEARCH_PAGE_LIMIT,
      offset: searchOffset,
    });

  // ページ取得結果を累積（offset=0 のときは置き換え、それ以外は追記して重複除外）
  useEffect(() => {
    const data = searchResults?.data;
    if (!data) return;
    setAccumulated((prev) => {
      if (data.offset === 0) return data.users;
      const seen = new Set(prev.map((u) => u.id));
      return [...prev, ...data.users.filter((u) => !seen.has(u.id))];
    });
  }, [searchResults]);

  const hasMore = !!searchResults?.data?.has_next;
  const totalCount = searchResults?.data?.count ?? 0;

  const loadMore = useCallback(() => {
    if (!hasMore || isSearching) return;
    setSearchOffset((prev) => prev + SEARCH_PAGE_LIMIT);
  }, [hasMore, isSearching]);

  // sentinel が可視領域に入ったら次ページ
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const candidates: FollowUser[] = accumulated;

  // ===== 選択・付与の状態 =====
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const selectedUser = useMemo(
    () => candidates.find((u) => u.id === selectedUserId) ?? null,
    [candidates, selectedUserId],
  );
  const [amountInput, setAmountInput] = useState("");
  const [note, setNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [creditUserBalance, { isLoading: isSubmitting }] =
    useCreditUserBalanceMutation();

  const amountNumber = useMemo(() => toYen(amountInput || "0"), [amountInput]);
  const canConfirm =
    selectedUserId !== null &&
    amountNumber >= MIN_CREDIT_AMOUNT &&
    !isSubmitting;

  const handleSelect = useCallback(
    (id: string | number) => {
      const numericId = Number(id);
      // AvatarStack の単一選択モードは「同じ id を再選択」されると同じ
      // コールバックが呼ばれるため、ここでトグル（=解除）を実装する。
      setSelectedUserId((prev) => (prev === numericId ? null : numericId));
    },
    [],
  );

  const handleOpenConfirm = () => {
    if (!canConfirm) return;
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (isSubmitting) return;
    setConfirmOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedUser) return;
    try {
      const response = await creditUserBalance({
        userId: selectedUser.id,
        amount: amountNumber,
        note: note.trim() || undefined,
      }).unwrap();
      const updated = response.data?.user?.balance_free;
      toast.success(
        `${selectedUser.display_name_jp} さんに ${formatYen(amountNumber)} を付与しました${
          updated ? `（balance_free=${updated}）` : ""
        }`,
      );
      setConfirmOpen(false);
      setSelectedUserId(null);
      setAmountInput("");
      setNote("");
      // 結果には影響しないが、検索結果を再フェッチしておく（offset 初期化）。
      setSearchOffset(0);
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "残高付与に失敗しました";
      toast.error(message);
    }
  };

  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }

  const role: string | undefined = currentUser?.data?.role;
  if (role !== "ADMIN") {
    return <Navigate to="/projects" replace />;
  }

  return (
    <PageWrapper>
      <TitleBlock
        title="残高付与"
        description="master_admin が委託者・受託者の balance_free（新モデル・税抜）を台帳経由で安全に増額します。Stripe を介さない初期予算・運用補填用です。"
      />

      <Section>
        <SectionHeader>
          <SectionTitle>1. ユーザーを選択</SectionTitle>
          <Count>
            ({candidates.length}
            {totalCount > candidates.length ? `/${totalCount}` : ""})
          </Count>
        </SectionHeader>
        <Hint>
          氏名・ステージネームで絞り込み（master_client / CREATOR 全員。
          master_admin / curator は対象外）。スクロールで追加読み込み。
        </Hint>
        <SearchBar>
          <Input
            type="search"
            placeholder="氏名・ステージネームで絞り込み"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
        {selectedUser && (
          <SelectedCard>
            <UserAvatar
              userId={selectedUser.id}
              avatar={selectedUser.avatar_url}
              displayName={selectedUser.display_name_jp}
              size={40}
            />
            <SelectedMeta>
              <SelectedName>{selectedUser.display_name_jp}</SelectedName>
              <SelectedSub>
                ID: {selectedUser.id}
                {selectedUser.title_jp ? ` / ${selectedUser.title_jp}` : ""}
              </SelectedSub>
            </SelectedMeta>
            <Button
              size="small"
              theme="tertiary"
              variant="text"
              onClick={() => setSelectedUserId(null)}
            >
              選択を解除
            </Button>
          </SelectedCard>
        )}
        {isSearching && candidates.length === 0 ? (
          <Hint>読み込み中...</Hint>
        ) : candidates.length === 0 ? (
          <Hint>該当するユーザーがいません。</Hint>
        ) : (
          <>
            <AvatarStack
              items={candidates.map(toAvatarStackItem)}
              size={40}
              selectedId={selectedUserId}
              onSelect={handleSelect}
              endSlot={hasMore ? <Sentinel ref={sentinelRef} /> : null}
              ariaLabel="付与対象ユーザー候補"
            />
            {isSearching && hasMore && <Hint>読み込み中...</Hint>}
          </>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>2. 付与額と理由</SectionTitle>
        </SectionHeader>
        <Hint>
          金額は税抜・整数円で指定します。下限 ¥{MIN_CREDIT_AMOUNT}。
          `balance_free` にそのまま加算され、`PaymentLedger` に
          `event_type=admin_credit` で監査ログが残ります。
        </Hint>

        <FieldRow>
          <FieldLabel text="付与額（税抜）" option="required" />
          <AmountInputRow>
            <MoneyInput
              placeholder="金額"
              suffix=""
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
            />
            <InlineUnit>円</InlineUnit>
          </AmountInputRow>
        </FieldRow>

        <FieldRow>
          <Input
            label="理由メモ"
            option="optional"
            placeholder="例: ◯◯プロジェクト 初期予算付与"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxlength={500}
          />
        </FieldRow>

        <div className="u-row u-justify-end u-w100">
          <Button
            size="medium"
            theme="primary"
            variant="filled"
            onClick={handleOpenConfirm}
            disabled={!canConfirm}
          >
            付与内容を確認
          </Button>
        </div>
      </Section>

      {confirmOpen && selectedUser && (
        <Modal
          title="残高付与の最終確認"
          description="この内容で対象ユーザーの balance_free を増額します。実行後は台帳に監査ログとして記録され、巻き戻すには admin_refund が必要です。"
          onClose={handleCloseConfirm}
          primaryAction={{
            label: "この内容で付与",
            onClick: () => void handleSubmit(),
            loading: isSubmitting,
            disabled: isSubmitting,
          }}
          secondaryAction={{
            label: "戻る",
            onClick: handleCloseConfirm,
            autoFocus: true,
          }}
        >
          <ConfirmList>
            <ConfirmRow>
              <ConfirmKey>対象ユーザー</ConfirmKey>
              <ConfirmValue>
                {selectedUser.display_name_jp}（ID: {selectedUser.id}）
              </ConfirmValue>
            </ConfirmRow>
            <ConfirmRow>
              <ConfirmKey>付与額（税抜）</ConfirmKey>
              <ConfirmValue>{formatYen(amountNumber)}</ConfirmValue>
            </ConfirmRow>
            <ConfirmRow>
              <ConfirmKey>反映バケット</ConfirmKey>
              <ConfirmValue>balance_free（新モデル）</ConfirmValue>
            </ConfirmRow>
            <ConfirmRow>
              <ConfirmKey>台帳イベント</ConfirmKey>
              <ConfirmValue>admin_credit（external → free）</ConfirmValue>
            </ConfirmRow>
            {note.trim() && (
              <ConfirmRow>
                <ConfirmKey>理由メモ</ConfirmKey>
                <ConfirmValue>{note.trim()}</ConfirmValue>
              </ConfirmRow>
            )}
          </ConfirmList>
        </Modal>
      )}
    </PageWrapper>
  );
};

export default AdminBalanceCredit;
